import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import { fetchPartners, resolveImg } from '../api';
import { useSiteConfig } from '../context/SiteConfigContext';

const CTASection = () => {
  const [partners, setPartners] = useState([]);
  const siteConfig = useSiteConfig();
  const stats = siteConfig.homeStats || { gymnasts: '200+', foundedYear: 2012, medals: '50+' };
  const yearsOfExcellence = Math.max(1, new Date().getFullYear() - (stats.foundedYear || 2012));
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    fetchPartners().then((data) => setPartners(data || [])).catch(() => {});
  }, []);

  return (
    <section className="relative py-32 overflow-hidden" ref={ref}>
      {/* Animated Background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/20 via-black to-pink-900/20" />
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-fuchsia-500/10 to-pink-500/10 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center"
        >
          {/* Main Heading */}
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            Prêt à rejoindre
            <br />
            <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-fuchsia-500 bg-clip-text text-transparent">
              l'aventure ?
            </span>
          </h2>

          <p className="text-xl md:text-2xl text-white/60 mb-12 max-w-3xl mx-auto leading-relaxed">
            Découvrez un sport complet qui développe élégance, souplesse et confiance en soi. 
            Inscrivez-vous dès maintenant pour la saison 2026-2027.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <motion.a
              href="/inscriptions"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="group relative px-10 py-5 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full text-white font-bold text-lg overflow-hidden shadow-lg shadow-fuchsia-500/50"
            >
              <span className="relative z-10 flex items-center gap-3">
                S'inscrire maintenant
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight size={20} />
                </motion.span>
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-pink-600 to-fuchsia-600"
                initial={{ x: '100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.a>
          </div>

          {/* Social Proof */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16 flex flex-wrap justify-center gap-12 text-center"
          >
            <div>
              <div className="text-4xl font-bold bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent mb-2">
                {stats.gymnasts || '200+'}
              </div>
              <div className="text-white/60 text-sm uppercase tracking-wider">Gymnastes</div>
            </div>
            <div className="w-px h-16 bg-white/10" />
            <div>
              <div className="text-4xl font-bold bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent mb-2">
                {yearsOfExcellence}
              </div>
              <div className="text-white/60 text-sm uppercase tracking-wider">Années d'Excellence</div>
            </div>
            <div className="w-px h-16 bg-white/10" />
            <div>
              <div className="text-4xl font-bold bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent mb-2">
                {stats.medals || '50+'}
              </div>
              <div className="text-white/60 text-sm uppercase tracking-wider">Médailles</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Partenaires Band */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="container mx-auto px-6 relative z-10 mt-24"
        data-testid="partners-section"
      >
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider">
            Nos Partenaires
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {partners.map((partner, index) => (
            <motion.div
              key={partner.id || index}
              whileHover={{ y: -6, scale: 1.03 }}
              className="group flex flex-col items-center justify-center text-center p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/50 transition-all duration-500"
              data-testid={`partner-card-${index}`}
            >
              <div className="w-24 h-24 flex items-center justify-center mb-4 overflow-hidden group-hover:scale-110 transition-transform duration-300">
                {partner.logo ? (
                  <img src={resolveImg(partner.logo)} alt={partner.name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center">
                    <span className="text-fuchsia-400 font-bold text-xl">
                      {partner.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              <h4 className="text-white font-bold text-sm mb-1">{partner.name}</h4>
              {partner.website && (
                <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-white/40 text-xs hover:text-fuchsia-400 transition-colors">
                  Visiter
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-fuchsia-500/50 to-transparent" />
    </section>
  );
};

export default CTASection;
